import { t } from "i18next";
import { ComponentProps } from "react";
import { ComputeFieldComponent } from "../computeFields";
import { ArtworkInBibliography } from "../types";
import { SeparatedList } from "./shared/utils";

export const BibliographyDescriptionCF: ComputeFieldComponent<
  Pick<
    ArtworkInBibliography,
    | "bibliography"
    | "oeuvresDansBibliographieDescriptionCitePage"
    | "oeuvresDansBibliographieDescriptionReproduitPage"
    | "oeuvresDansBibliographieDescriptionReproductionCouleur"
    | "oeuvresDansBibliographieDescriptionNonPagine"
  >
> = ({ input, language, ...props }) => {
  // Raccourcis pour les champs
  const isReprCoul =
    !!input.oeuvresDansBibliographieDescriptionReproductionCouleur;
  const hasReprPage = !!input.oeuvresDansBibliographieDescriptionReproduitPage;
  const isNonPagine = !!input.oeuvresDansBibliographieDescriptionNonPagine;

  // Générer la partie "reproduit", conditionnelle selon la logique
  let reproducePart: React.ReactNode = null;

  if (isReprCoul) {
    if (hasReprPage && !isNonPagine) {
      // Cas : repr. coul. VRAI & rep page VRAI > "repr. coul. p. {page}"
      reproducePart = (
        <>
          {t("computeField.reproduitCouleurAbbrege")}
          {" p. "}
          {input.oeuvresDansBibliographieDescriptionReproduitPage}
        </>
      );
    } else {
      // Cas : repr. coul. VRAI & (rep page FAUX OU non paginé) > "repr. coul., n. p."
      reproducePart = (
        <>
          {t("computeField.reproduitCouleurAbbrege")}
          {", "}
          {t("computeField.nonPagineAbbrege")}
        </>
      );
    }
  } else if (hasReprPage && !isNonPagine) {
    // Cas : couleur FAUX & rep page VRAI & non paginé FAUX > "repr. p. {page}"
    reproducePart = (
      <>
        {t("computeField.reproduitPageAbbrege")}{" "}
        {input.oeuvresDansBibliographieDescriptionReproduitPage}
      </>
    );
  } else if (isNonPagine && !hasReprPage) {
    // Cas : non paginé VRAI OU rep page FAUX > "n. p."
    reproducePart = <>{t("computeField.nonPagineAbbrege")}</>;
  }

  return (
    <>
      <SeparatedList
        {...props}
        lineBreak={false}
        language={language}
        commaSeparated
        input={[
          <span
            dangerouslySetInnerHTML={{
              __html: input.bibliography?.bibliographieDescriptionResumeLong,
            }}
          />,
          input.oeuvresDansBibliographieDescriptionCitePage && (
            <>
              {t("computeField.citePageAbbrege")}{" "}
              {input.oeuvresDansBibliographieDescriptionCitePage}
            </>
          ),
          reproducePart,
        ]}
      />
    </>
  );
};

export const RelatedBibliographiesCF: ComputeFieldComponent<{
  relatedBibliographies?: ComponentProps<
    typeof BibliographyDescriptionCF
  >["input"][];
}> = ({ input, lineBreak = true, ...props }) => {
  if (
    !input.relatedBibliographies ||
    input.relatedBibliographies.length === 0
  ) {
    return <></>;
  }
  return (
    <SeparatedList
      {...props}
      input={input.relatedBibliographies.map((bibliography, index) => (
        <BibliographyDescriptionCF
          key={index}
          input={bibliography}
          {...props}
        />
      ))}
      lineBreak={lineBreak}
    />
  );
};
